import {HttpClient, HttpClientModule} from '@angular/common/http';
import {SocketIoModule} from 'ngx-socket-io';
import {APP_INITIALIZER, NgModule, isDevMode, Injector} from '@angular/core';
import {LOCATION_INITIALIZED, registerLocaleData, DatePipe} from '@angular/common';
import { UntypedFormBuilder } from '@angular/forms';
import localeDe from '@angular/common/locales/de';
import {BrowserModule} from '@angular/platform-browser';
import {AndroidPermissions} from '@awesome-cordova-plugins/android-permissions/ngx';
import {IonicStorageModule} from '@ionic/storage';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {RouteReuseStrategy} from '@angular/router';
import {IonicModule, IonicRouteStrategy} from '@ionic/angular';
import {LoggingService} from 'ionic-logging-service';
import { LoggingViewerModule } from "ionic-logging-viewer";

import {SocketProvider} from './providers/socket/socket';
import {FcmProvider} from './providers/fcm/fcm';
import {PatientAlertProvider} from './providers/patient-alert/patient-alert';
import {PatientDataProvider} from './providers/patient-data/patient-data';
import {OpenCaseProvider} from './providers/open-case/open-case';
import {Settings} from './providers/settings/settings';
import {Api} from './providers/api/api';
import {PhonePanelProvider} from './providers/phone-panel/phone-panel';
import {HistoryProvider} from './providers/history/history';
import {AlertEventsProvider} from './providers/alert-events/alert-events';

import {TechnicalAlertProvider} from './providers/technical-alert/technical-alert';
import {SocketOneProvider} from './providers/socket-one/socket-one';
import {SocketTwoProvider} from './providers/socket-two/socket-two';
import {User} from './providers/user/user';

import {AngularFireModule} from '@angular/fire/compat';
import {AngularFirestoreModule, SETTINGS as FirestoreSettingsToken} from '@angular/fire/compat/firestore';
import {AngularFireAuthModule} from '@angular/fire/compat/auth';
import {AngularFireMessagingModule} from '@angular/fire/compat/messaging';
import {AfsProvider} from './providers/afs/afs';
import {SipProvider} from './providers/sip/sip';
import {DbProvider} from './providers/db/db';
import {ChatService} from './providers/chat-service';
import {SipRegisterProvider} from './providers/sip-register/sip-register';

import {AppComponent} from './app.component';
import {AppRoutingModule} from './app-routing.module';
import {ConfigurationService} from './providers/configuration.service';
import {EmojiProvider} from './providers/emoji';
import {Media} from '@awesome-cordova-plugins/media/ngx';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';

import { AudioService } from './providers/audio/audio.service';
import { ServiceWorkerModule } from '@angular/service-worker';


registerLocaleData(localeDe);

// The translate loader needs to know where to load i18n files
// in Ionic's static asset pipeline.
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function loadConfiguration(configurationService: ConfigurationService, afsProvider: AfsProvider, loggingService:LoggingService, translate:TranslateService, injector: Injector, socketOne:SocketOneProvider, socketTow:SocketTwoProvider) {
  return () => new Promise<void>(async (resolve) => {

    const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    const locale = translate.getBrowserLang();
    let cultureLang = translate.getBrowserCultureLang();

    if(cultureLang.indexOf("-") === -1) {
      cultureLang = cultureLang+"-"+cultureLang.toLocaleUpperCase();
    }

    localStorage.setItem('cultureLang', cultureLang);
    const languages: Array<string> = ['en', 'de', 'ru', 'uk', 'tr', 'es', 'fr', 'pl', '	bg', 'bs']; 
    const savedLanguage: string = localStorage.getItem('userlanguage');
    const lang: string = savedLanguage ? savedLanguage : (languages.indexOf(locale) !== -1 ? locale : languages[0]);
    import(`/node_modules/@angular/common/locales/${lang}.mjs`).then(module => registerLocaleData(module.default, cultureLang));

    await locationInitialized

    configurationService.load('./assets/settings.json').then(() => {
      afsProvider.getConfig().then(() => {
        loggingService.configure(configurationService.getValue('logging'));
        (socketOne as any).config.url = configurationService.getValue('socketUrl').toString();
        (socketTow as any).config.url = configurationService.getValue('secSocketUrl').toString();
        (socketOne as any).ioSocket.io.uri = (socketOne as any).config.url;
        (socketTow as any).ioSocket.io.uri = (socketTow as any).config.url;
        resolve();
      }).catch(() => {
        resolve();
      });
    });
  });
}

const firebaseConfig = {
  apiKey: 'AIzaSyCFWBnVG0YCgW60JPtUcJxhxfckUrKroTI',
  authDomain: 'hausnotruf-88ebb.firebaseapp.com',
  databaseURL: 'https://hausnotruf-88ebb.firebaseio.com',
  projectId: 'hausnotruf-88ebb',
  storageBucket: 'hausnotruf-88ebb.appspot.com',
  messagingSenderId: '493161744790',
  appId: '1:493161744790:web:0a1cbd1d76cdec160635b7'
};

@NgModule({
  declarations: [
    AppComponent
  ],
  bootstrap: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    IonicModule.forRoot({
      mode: 'ios'
    }),
    AppRoutingModule,
    IonicStorageModule.forRoot(),
    AngularFireModule.initializeApp(firebaseConfig),
    AngularFireAuthModule,
    AngularFirestoreModule,
    AngularFireMessagingModule,
    SocketIoModule,
    LoggingViewerModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    })
  ],
  providers: [
    {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},
    Api,
    User,
    AndroidPermissions,
    Settings,
    Media,
    ConfigurationService,
    {
      provide: APP_INITIALIZER,
      useFactory: loadConfiguration,
      deps: [ConfigurationService, AfsProvider, LoggingService, TranslateService, Injector, SocketOneProvider, SocketTwoProvider],
      multi: true
    },
    SocketProvider,
    LoggingService,
    PatientAlertProvider,
    PatientDataProvider,
    OpenCaseProvider,
    PhonePanelProvider,
    HistoryProvider,
    AlertEventsProvider,
    TechnicalAlertProvider,
    SocketOneProvider,
    SocketTwoProvider,
    {provide: FirestoreSettingsToken, useValue: {}},
    FcmProvider,
    AfsProvider,
    SipProvider,
    EmojiProvider,
    DbProvider,
    ChatService,
    SipRegisterProvider,
    UntypedFormBuilder,
    AudioService,
    DatePipe
  ]
})
export class AppModule {
}
